/**
 * `/trash` and `/archive` — the two account-level destinations in the avatar menu
 * (spec M1-B1 §7.5, §8; `[ui:library/shell-05-profile-menu.png]`).
 *
 * One component for both, because monday's two screens differ only in which timestamp they
 * show and whether **Delete permanently** is offered: the spec's columns are the same —
 * name · workspace · deleted/archived by · date · actions — and so is the empty state.
 *
 * The list is `boards.removed`, which resolves the workspace name and the actor's display
 * name server-side and answers `canRestore` per row, so this screen never computes a
 * permission itself (F8 §8) and never issues three queries to fill one table.
 */
import { Icon, formatMondayDateTime, icons, useConfirm, useToast } from '@elizmonday/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPCClient } from '../../lib/trpc';
import { useBoardMutations } from './hooks';
import './boards.css';

export type RemovedState = 'archived' | 'deleted';

interface RemovedBoard {
  id: string;
  name: string;
  workspaceId: string | null;
  workspaceName: string | null;
  byName: string | null;
  at: string | Date | null;
  canRestore: boolean;
}

const COPY: Record<
  RemovedState,
  { title: string; empty: string; byColumn: string; dateColumn: string; note: string }
> = {
  deleted: {
    title: 'Trash',
    empty: 'Nothing in the trash.',
    byColumn: 'Deleted by',
    dateColumn: 'Deleted',
    // ASSUMED window (B1 §12, question B1-5): monday's retention is not captured.
    note: 'Boards in the trash are restorable for 30 days.',
  },
  archived: {
    title: 'Archive',
    empty: 'Nothing archived.',
    byColumn: 'Archived by',
    dateColumn: 'Archived',
    note: 'An archived board keeps its data and stays readable at its URL.',
  },
};

/** `2026-09-10 14:03`, the account's locale, never a relative "3 days ago" we cannot verify. */
function formatWhen(at: string | Date | null): string {
  if (!at) return '—';
  const date = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(date.getTime())) return '—';
  return formatMondayDateTime(date, { hour: '2-digit', minute: '2-digit' });
}

export function RemovedBoardsScreen({ state }: { state: RemovedState }) {
  const client = useTRPCClient();
  const queryClient = useQueryClient();
  const mutations = useBoardMutations();
  const confirm = useConfirm();
  const toast = useToast();
  const copy = COPY[state];

  const rows = useQuery({
    queryKey: ['boards', 'removed', state],
    queryFn: () => client.boards.removed.query({ state }) as Promise<RemovedBoard[]>,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['boards'] });

  const restore = async (board: RemovedBoard) => {
    // Archive and trash are independent states with their own mutation (B1 §7.5).
    if (state === 'archived') await mutations.restore.mutateAsync(board.id);
    else await mutations.restoreFromTrash.mutateAsync(board.id);
    await refresh();
    toast(`${board.name} restored`, { kind: 'positive' });
  };

  const purge = async (board: RemovedBoard) => {
    const ok = await confirm({
      title: `Delete ${board.name} permanently?`,
      body: 'The board and every item, column and update in it are removed. This cannot be undone.',
      confirmLabel: 'Delete permanently',
      danger: true,
    });
    if (!ok) return;
    await client.boards.purge.mutate({ id: board.id });
    await refresh();
    toast(`${board.name} deleted permanently`, { kind: 'positive' });
  };

  const list = rows.data ?? [];

  return (
    <div className="ez-removed" data-testid={`${state === 'deleted' ? 'trash' : 'archive'}-screen`}>
      <header className="ez-removed__head">
        <h1 className="ez-removed__title">{copy.title}</h1>
        <p className="ez-removed__note">{copy.note}</p>
      </header>

      {rows.isPending ? (
        <p className="ez-removed__empty">Loading…</p>
      ) : list.length === 0 ? (
        <p className="ez-removed__empty" data-testid="removed-empty">
          {copy.empty}
        </p>
      ) : (
        <table className="ez-removed__table">
          <thead>
            <tr>
              <th scope="col">Board</th>
              <th scope="col">Workspace</th>
              <th scope="col">{copy.byColumn}</th>
              <th scope="col">{copy.dateColumn}</th>
              <th scope="col">
                <span className="ez-visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.map((board) => (
              <tr key={board.id} data-testid={`removed-${board.id}`}>
                <td>
                  <span className="ez-removed__name">
                    <Icon icon={icons.board} size={16} ignoreFocusStyle />
                    {board.name}
                  </span>
                </td>
                <td>{board.workspaceName ?? '—'}</td>
                <td>{board.byName ?? '—'}</td>
                <td>{formatWhen(board.at)}</td>
                <td className="ez-removed__actions">
                  {board.canRestore ? (
                    <>
                      <button
                        type="button"
                        className="ez-removed__action"
                        data-testid={`restore-${board.id}`}
                        onClick={() => void restore(board)}
                      >
                        Restore
                      </button>
                      {/* monday offers the permanent delete from the trash only. */}
                      {state === 'deleted' ? (
                        <button
                          type="button"
                          className="ez-removed__action ez-removed__action--danger"
                          data-testid={`purge-${board.id}`}
                          onClick={() => void purge(board)}
                        >
                          Delete permanently
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <span className="ez-removed__no-action">No access</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
